"use client";

/**
 * UploadZone — file upload UI with analysis type selection, currency picker,
 * progress display, and ProtectedFeature gating.
 */
import { useRef, useState } from "react";
import * as Select from "@radix-ui/react-select";
import { ChevronDown, Check } from "lucide-react";
import { ProtectedFeature } from "@/platform/auth/ProtectedFeature";
import { ANALYSIS_OPTIONS, type AnalysisState } from "../types";

const CURRENCIES = ["USD", "EUR", "GBP", "PKR", "AED", "INR", "CNY", "JPY"];
const CURRENCY_META: Record<string, { label: string; symbol: string }> = {
  USD: { label: "US Dollar",        symbol: "$"   },
  EUR: { label: "Euro",             symbol: "€"   },
  GBP: { label: "British Pound",    symbol: "£"   },
  PKR: { label: "Pakistani Rupee",  symbol: "Rs"  },
  AED: { label: "UAE Dirham",       symbol: "د.إ" },
  INR: { label: "Indian Rupee",     symbol: "₹"   },
  CNY: { label: "Chinese Yuan",     symbol: "¥"   },
  JPY: { label: "Japanese Yen",     symbol: "¥"   },
};

interface Props {
  analysisState: AnalysisState;
  selectedType: string;
  onTypeChange: (type: string) => void;
  onFileSelected: (file: File) => void;
  isAuthenticated: boolean;
}

export function UploadZone({
  analysisState,
  selectedType,
  onTypeChange,
  onFileSelected,
  isAuthenticated,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currency, setCurrency] = useState("USD");
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);

  const option = ANALYSIS_OPTIONS.find((o) => o.id === selectedType) ?? ANALYSIS_OPTIONS[0];

  function handleDownloadSample() {
    const sampleFiles: Record<string, { href: string; filename: string }> = {
      spend:     { href: "/data/analyse/spend-sample.csv",     filename: "spend-sample.csv"     },
      inventory: { href: "/data/analyse/inventory-sample.csv", filename: "inventory-sample.csv" },
      logistics: { href: "/data/analyse/logistics-sample.xlsx", filename: "logistics-sample.xlsx" },
      any:       { href: "/data/analyse/spend-sample.csv",     filename: "spend-sample.csv"     },
    };
    const file = sampleFiles[selectedType] ?? sampleFiles.any;
    const link = document.createElement("a");
    link.href = file.href;
    link.download = file.filename;
    link.click();
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      setFileName(file.name);
      onFileSelected(file);
      e.target.value = "";
    }
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    if (!isAuthenticated) return;
    const file = e.dataTransfer.files?.[0];
    if (file) {
      setFileName(file.name);
      onFileSelected(file);
    }
  }

  const isActive = analysisState.isProcessing || analysisState.isUploading;

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {/* Label above tabs */}
      <p style={{ margin: "0 0 10px", fontSize: 13, color: "var(--ink-mute)", letterSpacing: "0.02em", textAlign: "center" }}>
        Select your data type for a more focused analysis
      </p>

      {/* Tabs + drop zone overlap wrapper */}
      <div style={{ position: "relative" }}>
        {/* Tab bar — centred, sits so its bottom half overlaps the drop zone */}
        <div style={{ textAlign: "center", position: "relative", zIndex: 2, marginBottom: -26 }}>
          <div className="diag-tabs upload-tabs" style={{ marginBottom: 0 }}>
            {ANALYSIS_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                className={`diag-tab${selectedType === opt.id ? " active" : ""}`}
                onClick={() => onTypeChange(opt.id)}
              >
                {opt.id === "any"
                  ? "Any Data"
                  : opt.title
                      .replace(" Analytics", "")
                      .replace(" Health Check", "")
                      .replace(" Analysis", "")}
              </button>
            ))}
          </div>
        </div>

      {/* Drop zone — extra top padding so content clears the overlapping tab bar */}
      <div
        className="upload-zone"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        style={{
          paddingTop: 82,
          ...(dragOver ? { borderColor: "var(--indigo)", background: "rgba(91,108,255,0.06)" } : {}),
        }}
      >
        {/* Icon */}
        <div className="upload-icon">
          <FileDataIcon />
        </div>

        {/* Title + subtitle */}
        <h2 className="upload-title">{option.title}</h2>
        <p className="upload-sub">{option.description}</p>

        {/* Currency field */}
        <div className="upload-field">
          <label className="upload-label" htmlFor="analyse-currency">Currency</label>
          <Select.Root value={currency} onValueChange={setCurrency}>
            <Select.Trigger
              id="analyse-currency"
              className="currency-trigger"
              aria-label="Select currency"
            >
              <span className="currency-trigger__symbol">
                {CURRENCY_META[currency].symbol}
              </span>
              <span className="currency-trigger__code">{currency}</span>
              <span className="currency-trigger__label">
                — {CURRENCY_META[currency].label}
              </span>
              <Select.Icon asChild>
                <ChevronDown size={14} className="currency-trigger__chevron" />
              </Select.Icon>
            </Select.Trigger>

            <Select.Portal>
              <Select.Content className="currency-content" position="popper" sideOffset={6}>
                <Select.Viewport className="currency-viewport">
                  {CURRENCIES.map((c) => (
                    <Select.Item key={c} value={c} className="currency-item">
                      <span className="currency-item__symbol">{CURRENCY_META[c].symbol}</span>
                      <span className="currency-item__code">{c}</span>
                      <span className="currency-item__name">{CURRENCY_META[c].label}</span>
                      <Select.ItemIndicator className="currency-item__check">
                        <Check size={12} />
                      </Select.ItemIndicator>
                    </Select.Item>
                  ))}
                </Select.Viewport>
              </Select.Content>
            </Select.Portal>
          </Select.Root>
        </div>

        {/* Progress */}
        {isActive && (
          <div style={{ marginBottom: 24, maxWidth: 440, marginInline: "auto" }}>
            <div style={{
              height: 6, borderRadius: 3,
              background: "var(--paper-2)",
              overflow: "hidden", marginBottom: 8,
            }}>
              <div style={{
                height: "100%", borderRadius: 3,
                background: "linear-gradient(90deg, var(--indigo), #8b5cf6)",
                width: `${analysisState.progress}%`,
                transition: "width 0.4s ease",
              }} />
            </div>
            <p style={{ margin: 0, fontSize: 13, color: "var(--ink-soft)" }}>
              {analysisState.progressMessage} — {Math.round(analysisState.progress)}%
            </p>
          </div>
        )}

        {/* Error */}
        {analysisState.error && (
          <div style={{
            marginBottom: 20, padding: "10px 16px", borderRadius: 8,
            background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.2)",
            fontSize: 13, color: "#ef4444", maxWidth: 480, marginInline: "auto",
          }}>
            {analysisState.error}
          </div>
        )}

        {/* Action buttons */}
        <div className="upload-actions">
          <ProtectedFeature>
            <button
              className="upl-primary"
              onClick={() => fileInputRef.current?.click()}
              disabled={isActive}
              style={isActive ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
            >
              {isActive ? <SpinnerIcon /> : <UploadArrowIcon />}
              {isActive ? "Processing…" : "Upload Data"}
            </button>
          </ProtectedFeature>

          <button className="upl-ghost" onClick={handleDownloadSample}>
            <DownloadIcon />
            Download Sample
          </button>
        </div>

        <p className="upload-note">Max file size allowed: 50 MB · CSV and Excel supported</p>

        {fileName && (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              marginTop: 12,
              padding: "6px 14px",
              borderRadius: 999,
              background: "rgba(91,108,255,0.1)",
              border: "1px solid rgba(91,108,255,0.25)",
              fontSize: 13,
              fontWeight: 600,
              color: "var(--indigo)",
              wordBreak: "break-all",
              maxWidth: 480,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
            </svg>
            {fileName}
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileChange}
          style={{ display: "none" }}
          aria-label="Upload data file"
        />
      </div>
      </div>
    </div>
  );
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function FileDataIcon() {
  return (
    <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="8" y1="13" x2="10" y2="13"/>
      <line x1="8" y1="17" x2="10" y2="17"/>
      <line x1="14" y1="13" x2="16" y2="13"/>
      <line x1="14" y1="17" x2="16" y2="17"/>
    </svg>
  );
}

function CurrencyIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="12" y1="1" x2="12" y2="23"/>
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
    </svg>
  );
}

function UploadArrowIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="17 8 12 3 7 8"/>
      <line x1="12" y1="3" x2="12" y2="15"/>
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: "spin 1s linear infinite" }} aria-hidden="true">
      <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
    </svg>
  );
}
